<ds-modal class="modal-container" [modal]="modal" [title]="'edit_finding' | translate" [closeAllModals]="false">
  <form class="details" [formGroup]="form">
    <!-- rule details card -->
    <div id="ruleDetails" class="margin-top">
      @if (serverResponse$ | async; as serverResponse) {
        <app-editable-manual-rule-details
          [formValidationRequest$]="formValidationRequest$"
          [issueResponse]="serverResponse"
          [form]="form"
          [field]="$auditIssueFields.ruleId"
          [testGuideId]="testGuideId$ | async"
          [customSeveritiesData]="customSeveritiesData"
          [customSeverityKeysSortedByRank]="customSeverityKeysSortedByRank"
          (selectedRuleChange)="onUpdateSelectedRule($event)"
        ></app-editable-manual-rule-details>
      }
    </div>
    <fieldset class="margin-bottom margin-top">
      <div class="title margin-bottom-md">
        <span class="text">{{ 'issue_edit_prompt' | translate }}</span>
      </div>

      <span class="margin-bottom-md">{{ 'required_fields_label' | translate }}</span>

      @if (canToggleDraft()) {
        <div class="my-3">
          <ds-toggle
            [label]="'save_as_draft' | translate"
            [labelPosition]="DsToggleLabelPosition.AFTER"
            [checked]="isDraft()"
            (valueChange)="onDraftToggleChange($event)"
            data-test-id="save-as-draft"
          ></ds-toggle>
        </div>
      }

      <ds-input
        class="d-block margin-bottom"
        [label]="'issue_field_summary' | translate"
        [required]="true"
        [errorMessage]="errors[$auditIssueFields.summary] | async"
        [errorState]="(errors[$auditIssueFields.summary] | async)?.length > 0"
        data-test-id="summary"
      >
        <div class="d-flex align-items-center gap-1 w-100">
          @if (isDraft() && canToggleDraft()) {
            <span class="draft-title-prefix" data-test-id="draft-title-prefix">{{ DRAFT_FINDING_TITLE_PREFIX }}</span>
          }
          <input ds-input-field [formControlName]="$auditIssueFields.summary" [id]="$auditIssueFields.summary" type="text" />
        </div>
      </ds-input>

      <ds-input
        class="d-block margin-bottom"
        [label]="'issue_field_screen' | translate"
        [required]="true"
        [errorMessage]="errors[$auditIssueFields.screen] | async"
        [errorState]="(errors[$auditIssueFields.screen] | async)?.length > 0"
        data-test-id="screen"
      >
        <input ds-input-field [formControlName]="$auditIssueFields.screen" [id]="$auditIssueFields.screen" type="text" />
      </ds-input>

      <ds-input
        class="d-block margin-bottom"
        [label]="'issue_field_url' | translate"
        [required]="true"
        [errorMessage]="errors[$auditIssueFields.url] | async"
        [errorState]="(errors[$auditIssueFields.url] | async)?.length > 0"
        data-test-id="url"
      >
        <input ds-input-field [formControlName]="$auditIssueFields.url" [id]="$auditIssueFields.url" type="url" />
      </ds-input>

      @if (hasManualEvaluationScope$ | async) {
        <div class="d-flex gap-1 mb-3 row">
          <ds-radio-group
            [label]="'is_this_finding_related_to_component' | translate"
            [required]="true"
            formControlName="isRelatedToComponent"
            class="col-12"
            data-test-id="is-related-to-component"
          >
            <ds-radio [value]="false" [label]="'no' | translate"></ds-radio>
            <ds-radio [value]="true" [label]="'yes' | translate"></ds-radio>
          </ds-radio-group>
          <div class="col-sm-12 col-md-6">
            @if (form.get('isRelatedToComponent').value) {
              <ds-input
                [label]="'component' | translate"
                [required]="true"
                [errorMessage]="errors[$auditFinding.componentId] | async"
                [errorState]="(errors[$auditFinding.componentId] | async)?.length > 0"
                data-test-id="component"
              >
                <select
                  ds-input-field
                  [formControlName]="$auditFinding.componentId"
                  class="mw-100"
                  [id]="$auditFinding.componentId"
                >
                  <option [ngValue]="null" disabled>{{ 'select_an_option' | translate }}</option>
                  @for (component of components$ | async; track component[$matchComponent._id]) {
                    <option [value]="component[$matchComponent._id]">
                      {{ component[$matchComponent.name] }}
                    </option>
                  }
                </select>
              </ds-input>
            }
          </div>
        </div>
      }

      <ds-input
        class="d-block margin-bottom"
        [label]="'issue_field_issueFrequency' | translate"
        [required]="true"
        [errorMessage]="errors[$auditIssueFields.issueFrequency] | async"
        [errorState]="(errors[$auditIssueFields.issueFrequency] | async)?.length > 0"
        data-test-id="issue-frequency"
      >
        <select
          ds-input-field
          [formControlName]="$auditIssueFields.issueFrequency"
          class="mw-100"
          [id]="$auditIssueFields.issueFrequency"
        >
          <option [ngValue]="null" disabled>{{ 'select_an_option' | translate }}</option>
          @for (frequency of auditFindingIssueFrequencyValues; track frequency.value) {
            <option [value]="frequency.value">
              {{ frequency.name | translate }}
            </option>
          }
        </select>
      </ds-input>

      <ds-input
        class="d-block margin-bottom"
        [label]="'label_issue_effort' | translate"
        [required]="true"
        [errorMessage]="errors[$auditIssueFields.effort] | async"
        [errorState]="(errors[$auditIssueFields.effort] | async)?.length > 0"
        data-test-id="issue-effort"
      >
        <select ds-input-field [formControlName]="$auditIssueFields.effort" class="mw-100" [id]="$auditIssueFields.effort">
          <option [ngValue]="null" disabled>{{ 'select_an_option' | translate }}</option>
          @for (effort of auditFindingIssueEffortValues; track $index) {
            <option [value]="effort.value">
              {{ effort.name | translate }}
            </option>
          }
        </select>
      </ds-input>
      <ds-input [label]="'issue_field_severity' | translate" [required]="true" class="mb-3" data-test-id="severity">
        <select ds-input-field [formControlName]="$auditIssueFields.severity" class="mw-100" [id]="$auditIssueFields.severity">
          @for (severity of auditFindingIssueSeverityValues$ | async; track $index) {
            <option [value]="severity.value" [disabled]="severity.isCustom">
              {{ severity.name }}
            </option>
          }
        </select>
      </ds-input>

      <fieldset data-test-id="viewport-resolution">
        <legend class="font-size-normal label margin-bottom-xs text-nowrap ps-0">
          {{ 'viewport_resolution_label' | translate }}
        </legend>
        <div class="d-flex gap-3">
          <ds-input
            class="d-block margin-bottom flex-1"
            [label]="('width_label' | translate) + ' (' + ('pixel_label' | translate) + ')'"
            [required]="false"
            [errorMessage]="errors[$auditFindingViewport.width] | async"
            [errorState]="(errors[$auditFindingViewport.width] | async)?.length > 0"
            data-test-id="viewport-width"
          >
            <input
              ds-input-field
              [formControlName]="$auditFindingViewport.width"
              [id]="$auditFindingViewport.width"
              type="number"
              [min]="viewportWidthMinValue"
              [max]="viewportWidthMaxValue"
            />
          </ds-input>
          <ds-input
            class="d-block margin-bottom flex-1"
            [label]="('height_label' | translate) + ' (' + ('pixel_label' | translate) + ')'"
            [required]="false"
            [errorMessage]="errors[$auditFindingViewport.height] | async"
            [errorState]="(errors[$auditFindingViewport.height] | async)?.length > 0"
            data-test-id="viewport-height"
          >
            <input
              ds-input-field
              [formControlName]="$auditFindingViewport.height"
              [id]="$auditFindingViewport.height"
              type="number"
              [min]="viewportHeightMinValue"
              [max]="viewportHeightMaxValue"
            />
          </ds-input>
        </div>
      </fieldset>
    </fieldset>
    <fieldset class="margin-bottom margin-top">
      <div class="title margin-bottom-md">
        <span class="text">{{ 'issue_reproduce_prompt' | translate }}</span>
      </div>

      <ds-input
        class="d-block margin-bottom"
        [label]="'issue_field_instances' | translate"
        [required]="false"
        [errorMessage]="errors[$auditIssueFields.instances] | async"
        [errorState]="(errors[$auditIssueFields.instances] | async)?.length > 0"
        data-test-id="instances"
      >
        <textarea
          ds-input-field
          [formControlName]="$auditIssueFields.instances"
          [id]="$auditIssueFields.instances"
          rows="8"
        ></textarea>
      </ds-input>

      <ds-input
        class="d-block margin-bottom"
        [label]="'label_screenshot_urls' | translate"
        [required]="false"
        [errorMessage]="errors[$auditIssueFields.screenshot] | async"
        [errorState]="(errors[$auditIssueFields.screenshot] | async)?.length > 0"
        data-test-id="screenshot"
      >
        <textarea
          ds-input-field
          [formControlName]="$auditIssueFields.screenshot"
          [id]="$auditIssueFields.screenshotManualEdit"
          rows="8"
        ></textarea>
      </ds-input>

      <div class="d-flex flex-column align-items-start available-domains margin-bottom">
        <span class="available-domains-heading">{{ 'label_available_domains' | translate }}</span>
        <span>*.essentialaccessibility.com</span>
        <span>*.screencast.com</span>
      </div>

      <div class="row">
        <div class="col-12">
          <app-file-drag-and-drop
            [form]="form"
            [formValidationRequest$]="formValidationRequest$"
            [existingUploads]="serverResponse?.attachment || []"
            (fileChange)="uploadFiles($event)"
            (existingUploadsChange)="onExistingAttachmentsChange($event)"
            [context]="fileUploadContext"
          ></app-file-drag-and-drop>
        </div>
      </div>

      <ds-input
        class="d-block margin-bottom"
        [label]="'label_actual_result' | translate"
        [required]="false"
        [errorMessage]="errors[$auditIssueFields.actualResult] | async"
        [errorState]="(errors[$auditIssueFields.actualResult] | async)?.length > 0"
        data-test-id="actual-result"
      >
        <textarea
          ds-input-field
          [formControlName]="$auditIssueFields.actualResult"
          [id]="$auditIssueFields.actualResult"
          rows="8"
        ></textarea>
      </ds-input>

      <ds-input
        class="d-block margin-bottom"
        [label]="'issue_field_stepsToReproduce' | translate"
        [required]="false"
        [errorMessage]="errors[$auditIssueFields.stepsToReproduce] | async"
        [errorState]="(errors[$auditIssueFields.stepsToReproduce] | async)?.length > 0"
        data-test-id="steps-to-reproduce"
      >
        <textarea
          ds-input-field
          [formControlName]="$auditIssueFields.stepsToReproduce"
          [id]="$auditIssueFields.stepsToReproduce"
          rows="8"
        ></textarea>
      </ds-input>

      <ds-input
        class="d-block margin-bottom"
        [label]="'label_user_impact' | translate"
        [required]="false"
        [errorMessage]="errors[$auditIssueFields.userImpact] | async"
        [errorState]="(errors[$auditIssueFields.userImpact] | async)?.length > 0"
        data-test-id="user-impact"
      >
        <textarea
          ds-input-field
          [formControlName]="$auditIssueFields.userImpact"
          [id]="$auditIssueFields.userImpact"
          rows="8"
        ></textarea>
      </ds-input>

      <ds-input
        class="d-block margin-bottom"
        [label]="'issue_field_recommendation' | translate"
        [required]="false"
        [errorMessage]="errors[$auditIssueFields.recommendation] | async"
        [errorState]="(errors[$auditIssueFields.recommendation] | async)?.length > 0"
        data-test-id="recommendation"
      >
        <textarea
          ds-input-field
          [formControlName]="$auditIssueFields.recommendation"
          [id]="$auditIssueFields.recommendation"
          rows="8"
        ></textarea>
      </ds-input>
    </fieldset>
  </form>
  <ng-template ds-modal-slide-out-footer>
    <ds-button-group
      [primaryAction]="{ label: 'form_save' | translate }"
      [secondAction]="{ label: 'form_cancel' | translate, variant: DsButtonVariants.secondary }"
      (primaryActionClicked)="submitForm()"
      (secondActionClicked)="modalService.closeCurrentModal()"
    >
    </ds-button-group>
  </ng-template>
</ds-modal>
